import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { cssVar } from "../colors";
import { newId } from "../model/ids";
import { withHole } from "../model/projectOps";
import { translateHoleInProject } from "./SelectTool";
import { resolveDrawTarget, toOriginalSpace } from "./drawTarget";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapResult,
  type DrawSnapTargets,
} from "./drawSnap";
import type { Point2 } from "./dragSnap";
import { placementGuideRays } from "./placementGuides";

const HOLE_GHOST_STROKE = () => cssVar("--piece-cut");

/**
 * Click to punch a hole into the part under the cursor. Shows a circle ghost
 * under the cursor at the current radius so the user can see exactly what
 * will be punched. Rejects the click if the hole's footprint lies entirely
 * outside every part — same rule as StampTool.
 */
export class HoleTool extends ToolBase {
  id = "hole";
  label = "Hole";
  shortcut = "h";
  cursor = "crosshair";

  private ghost: paper.Path | null = null;
  private ghostKey: string | null = null;
  private lastCursor: { x: number; y: number } | null = null;
  private unsubscribeSettings: (() => void) | null = null;

  // Snap targets (existing vertices / hole centres) gathered lazily and
  // invalidated whenever the project changes (#761). Rebuilt on next use.
  private snapTargets: DrawSnapTargets | null = null;

  // Drag-existing state — when the user mousedowns on an existing hole, we
  // start dragging it instead of placing a new one.
  private dragPieceId: string | null = null;
  private dragHoleId: string | null = null;
  private draggedItem: paper.Item | null = null;
  private dragStart: { x: number; y: number } | null = null;
  private dragLast: { x: number; y: number } | null = null;

  override onActivate() {
    this.snapTargets = null;
    this.unsubscribeSettings = this.ctx.onSettingsChanged(() => {
      // Radius slider was nudged — rebuild the ghost in place.
      if (this.lastCursor) this.updateGhost(this.lastCursor);
    });
  }

  override onDeactivate() {
    this.unsubscribeSettings?.();
    this.unsubscribeSettings = null;
    this.clearGhost();
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.setGuideRays(null);
    this.resetDrag();
    this.lastCursor = null;
  }

  /** Show the edge-distance + spacing guide rays (#918) for a placement at
   *  DISPLAYED-space `displayPoint`. Resolves the target part the same way a
   *  click would (unless one is supplied — the drag-existing case). Clears the
   *  rays when the point is over no part. */
  private refreshGuides(
    displayPoint: { x: number; y: number },
    excludeHoleId?: string | null,
    pieceId?: string,
  ) {
    const scope = this.ctx.scene.getPaperScope();
    const view = this.ctx.scene.getPartLayoutView();
    let pid = pieceId;
    if (!pid) {
      const radius = this.ctx.settings.holeRadius;
      const holeBounds = new scope.Rectangle({
        x: displayPoint.x - radius,
        y: displayPoint.y - radius,
        width: radius * 2,
        height: radius * 2,
      });
      pid = resolveDrawTarget(
        this.ctx.history.state,
        displayPoint,
        scope,
        this.ctx.scene.getSelection().pieceId,
        view,
        holeBounds,
      )?.pieceId;
    }
    if (!pid) {
      this.ctx.scene.setGuideRays(null);
      return;
    }
    const rays = placementGuideRays(
      this.ctx.history.state,
      displayPoint,
      pid,
      scope,
      view,
      excludeHoleId ? { excludeHoleId } : undefined,
    );
    this.ctx.scene.setGuideRays(rays.length ? rays : null);
  }

  /** Snap a raw cursor point onto a nearby vertex / the grid and light the
   *  in-draw snap indicator. Targets are cached until the project changes. */
  private snap(p: Point2): DrawSnapResult {
    if (!this.snapTargets) {
      const scope = this.ctx.scene.getPaperScope();
      this.snapTargets = gatherDrawSnapTargets(
        this.ctx.history.state,
        scope,
        this.ctx.scene.getPartLayoutView(),
      );
    }
    const result = resolveDrawSnap(this.ctx, p, this.snapTargets);
    this.ctx.scene.setDrawSnapPoint(result.hit?.pos ?? null);
    return result;
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    // Set "move" cursor when over an existing hole so the user knows they
    // can drag instead of placing a new one.
    const scope = this.ctx.scene.getPaperScope();
    const hit = this.ctx.scene.hitTest(new scope.Point(p.x, p.y));
    const overHole = !!hit.holeId;
    setCanvasCursor(scope, overHole ? "move" : "crosshair");
    if (overHole) {
      this.clearGhost();
      this.ctx.scene.setDrawSnapPoint(null);
      this.ctx.scene.setGuideRays(null);
    } else {
      const snapped = this.snap(p);
      this.updateGhost(snapped.point);
      this.refreshGuides(snapped.point);
    }
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    if (this.dragLast && this.draggedItem) {
      const dx = p.x - this.dragLast.x;
      const dy = p.y - this.dragLast.y;
      if (dx !== 0 || dy !== 0) {
        const scope = this.ctx.scene.getPaperScope();
        this.draggedItem.translate(new scope.Point(dx, dy));
        this.dragLast = { x: p.x, y: p.y };
      }
      // Guides for the hole being dragged — excluded from the spacing search so
      // it isn't measured against itself.
      if (this.dragPieceId) {
        this.refreshGuides(p, this.dragHoleId, this.dragPieceId);
      }
      return;
    }
    const snapped = this.snap(p).point;
    this.updateGhost(snapped);
    this.refreshGuides(snapped);
  }

  override onCancel() {
    if (this.draggedItem) this.ctx.rerender();
    this.resetDrag();
    this.clearGhost();
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.setGuideRays(null);
  }

  override onUp(_s: PointerSample, p: { x: number; y: number }) {
    this.ctx.scene.setGuideRays(null);
    if (!this.dragStart || !this.dragHoleId || !this.dragPieceId) return;
    const dx = p.x - this.dragStart.x;
    const dy = p.y - this.dragStart.y;
    const pieceId = this.dragPieceId;
    const holeId = this.dragHoleId;
    this.resetDrag();
    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return;
    this.snapTargets = null; // geometry changed — rebuild targets next use
    this.ctx.commit("Move hole", (proj) =>
      translateHoleInProject(proj, pieceId, holeId, dx, dy),
    );
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);
    const radius = this.ctx.settings.holeRadius;

    // If the user clicked an existing hole, start moving it instead of
    // placing a new one.
    const hit = this.ctx.scene.hitTest(pt);
    if (hit.holeId && hit.pieceId) {
      this.dragPieceId = hit.pieceId;
      this.dragHoleId = hit.holeId;
      this.dragStart = { x: p.x, y: p.y };
      this.dragLast = { x: p.x, y: p.y };
      this.clearGhost();
      // setSelection re-renders the scene, so look up the paper item
      // afterwards — otherwise we'd hold a reference to a detached item
      // and translating it during onMove would be a no-op on screen.
      this.ctx.setSelection({
        pieceId: hit.pieceId,
        decorationId: null,
        holeId: hit.holeId,
      });
      this.draggedItem = this.ctx.scene.getHoleItem(hit.pieceId, hit.holeId);
      return;
    }

    // Snap the placement point onto a nearby vertex / grid intersection.
    const snapped = this.snap(p).point;
    const project = this.ctx.history.state;
    const holeBounds = new scope.Rectangle({
      x: snapped.x - radius,
      y: snapped.y - radius,
      width: radius * 2,
      height: radius * 2,
    });
    // Resolve the target part (#783): never hidden, prefer the selected part,
    // else the top-most visible part under the cursor — not the bottom-most.
    const target = resolveDrawTarget(
      project,
      { x: snapped.x, y: snapped.y },
      scope,
      this.ctx.scene.getSelection().pieceId,
      this.ctx.scene.getPartLayoutView(),
      holeBounds,
    );
    if (!target) return;
    const { pieceId, offset } = target;
    // Store in the part's ORIGINAL space; the renderer re-applies the spread
    // offset, so a displayed-space position would be double-offset (#786).
    const position = toOriginalSpace(snapped, offset);

    this.snapTargets = null; // geometry changes — rebuild targets next use
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.setGuideRays(null);
    this.ctx.commit("Add hole", (proj) =>
      withHole(proj, pieceId, {
        id: newId("hole"),
        position,
        radius,
      }),
    );
  }

  private updateGhost(p: { x: number; y: number }) {
    const radius = this.ctx.settings.holeRadius;
    if (radius <= 0) {
      this.clearGhost();
      return;
    }
    const scope = this.ctx.scene.getPaperScope();
    const key = String(radius);
    if (this.ghostKey !== key || !this.ghost || !this.ghost.parent) {
      this.clearGhost();
      const circle = new scope.Path.Circle(new scope.Point(0, 0), radius);
      circle.strokeColor = new scope.Color(HOLE_GHOST_STROKE());
      circle.strokeScaling = false;
      circle.strokeWidth = 3;
      circle.fillColor = new scope.Color(0, 0, 0, 0.25);
      circle.opacity = 0.5;
      circle.data = { ephemeral: true };
      scope.project.activeLayer?.addChild(circle);
      this.ghost = circle;
      this.ghostKey = key;
    }
    if (this.ghost) {
      this.ghost.position = new scope.Point(p.x, p.y);
    }
  }

  private clearGhost() {
    this.ghost?.remove();
    this.ghost = null;
    this.ghostKey = null;
  }

  private resetDrag() {
    this.dragPieceId = null;
    this.dragHoleId = null;
    this.draggedItem = null;
    this.dragStart = null;
    this.dragLast = null;
  }
}

function setCanvasCursor(scope: paper.PaperScope, cursor: string) {
  const el = scope.view?.element as HTMLCanvasElement | undefined;
  if (el) el.style.cursor = cursor;
}
